import { Injectable, inject } from '@angular/core';
import { IFindRequestParams } from '@df/api/request/request.service';
import { Attribute } from '@df/catalog/attribute/attribute';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Resource } from '@df/catalog/resource';
import { WINDOW } from '@df/dom/globals';
import isEmpty from 'lodash-es/isEmpty';
import matches from 'lodash-es/matches';
import { TLoadParams } from '../model';

declare module '@df/catalog/resource' {
    interface IResources {
        attribute: AttributeResource;
    }
}

@Injectable({
    providedIn: 'root'
})
export class AttributeResource extends Resource<Attribute, string> {
    static override instance = Attribute;

    /**
     * You can manually set what attributes are to be loaded on particular verbosity levels
     * When this is set we load only given attributes
     */
    static v1Attrs: string[] | undefined;
    static v2Attrs: string[] | undefined;
    static v2AttrsAdd: string[] | undefined;
    static v3Attrs: string[] | undefined;

    readonly invalidatesUniversal = false;
    readonly invalidatesPrerender = false;

    /**
     * If verbosity level attributes have been loaded
     */
    private verbosityLoaded = 0;

    private promises: { [verbosity: number]: Promise<any> | undefined } = {};
    private window = inject(WINDOW);

    protected constructor() {
        super();

        const sources = (this.window.autoInitData as any)?.data?.attribute;

        if (sources) {
            this.verbosityLoaded = 4;

            sources.forEach((attributeData: Catalog.IAttributeData) =>
                this.app.resources.attribute.getCreate(attributeData.code).setData(attributeData)
            );
        }
    }

    /**
     * Loads attribute required for given verbosity
     */
    loadForProduct(verbosity: number): Promise<any> | void {
        if (this.verbosityLoaded >= verbosity) {
            return;
        }

        if (this.promises[verbosity]) {
            return this.promises[verbosity];
        }
        const getPromise = () => {
            if (verbosity > 2) {
                if (AttributeResource.v3Attrs) {
                    return this.loadAllIds(AttributeResource.v3Attrs, 1, { safe: true });
                }

                return this.loadAll();
            }

            if (verbosity > 1) {
                if (AttributeResource.v2Attrs) {
                    return this.loadAllIds(AttributeResource.v2Attrs, 1, { safe: true });
                }

                const promise = this.find({ filter: { $or: [{ filterable: true }, { configurable: true }] }, verbosity: 1 });

                if (AttributeResource.v2AttrsAdd) {
                    return Promise.all([promise, this.loadAllIds(AttributeResource.v2AttrsAdd, 1, { safe: true })]);
                }

                return promise;
            }

            if (AttributeResource.v1Attrs) {
                return this.loadAllIds(AttributeResource.v1Attrs, 1, { safe: true });
            }
        };

        const promise = getPromise();

        if (promise) {
            promise.then(() => {
                if (this.verbosityLoaded < verbosity) {
                    this.verbosityLoaded = verbosity;
                }
            });
        }

        this.promises[verbosity] = promise;

        return promise;
    }

    protected override findCallback(type: string, findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<Attribute[]> {
        if ((this.window.autoInitData as any)?.data?.attribute && !this.app.dfConfig.integration.noInit) {
            return new Promise(resolve => {
                const matcher = !isEmpty(findOptions.filter) && matches(findOptions.filter);
                const all = this.getAllExisting();

                resolve(matcher ? all.filter(i => matcher(i.data)) : all);
            });
        }

        return super.findCallback(type, findOptions, params);
    }
}
